import React, { useState, useEffect } from 'react';
import { Receipt, DollarSign, FileText, TrendingUp, CreditCard, Download, Mail, MoreVertical, Eye, ChevronDown } from 'lucide-react';
// Removed client-side PDF rendering to avoid Buffer dependency
import { useQuery } from '@tanstack/react-query';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import InvoicePage from '../invoice/InvoicePage';
import { apiService } from '../../services/api';
import EstimatePanel from './EstimatePanel';
import { InvoiceDto, WorkOrderDocumentationChecklist, WorkOrderVendor } from '../../types';
import {
  canCreateWorkOrderInvoice,
  INVOICE_ALLOWED_STATUSES,
  isDocumentationStatusName,
} from '../../utils/workOrderDocumentation';

interface Invoice {
  id: number;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate?: string;
  total: number;
  paidAmount?: number;
  status: string;
  invoiceType?: string;
  sentAt?: string;
  paidAt?: string;
}

interface FinancialTabProps {
  workOrderId: number;
  vendors: WorkOrderVendor[];
  defaultClientEmail?: string;
  invoices: Invoice[];
  invoiceSummary: {
    count: number;
    totalInvoiced: number;
    totalPaid: number;
    totalCredits: number;
    outstanding: number;
  };
  nteAmount?: number;
  statusName?: string;
  documentationChecklist?: WorkOrderDocumentationChecklist | null;
  onCreateDepositInvoice: () => void;
  onCreateProgressInvoice: () => void;
  onCreateFinalInvoice: () => void;
  onCreateChangeOrderInvoice: () => void;
  onViewInvoice: (invoiceId: number) => void;
}

const FinancialTab: React.FC<FinancialTabProps> = ({
  workOrderId,
  vendors,
  defaultClientEmail,
  invoices,
  invoiceSummary,
  nteAmount,
  statusName,
  documentationChecklist,
  onCreateDepositInvoice,
  onCreateProgressInvoice,
  onCreateFinalInvoice,
  onCreateChangeOrderInvoice,
  onViewInvoice,
}) => {
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [sendingId, setSendingId] = useState<number | null>(null);
  const [activeDropdownId, setActiveDropdownId] = useState<string | null>(null);
  const [showInvoiceDropdown, setShowInvoiceDropdown] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (activeDropdownId) {
        setActiveDropdownId(null);
      }
      if (showInvoiceDropdown) {
        setShowInvoiceDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [activeDropdownId, showInvoiceDropdown]);

  // Determine suggested invoice type based on current state
  const getSuggestedInvoiceType = () => {
    if (invoices.length === 0) return 'deposit';
    const hasDeposit = invoices.some(inv => inv.invoiceType === 'Deposit');
    const hasFinal = invoices.some(inv => inv.invoiceType === 'Final');
    
    if (hasDeposit && !hasFinal) return 'progress';
    if (hasDeposit && hasFinal) return 'change-order';
    return 'deposit';
  };

  const suggestedType = getSuggestedInvoiceType();
  const canCreateInvoice = canCreateWorkOrderInvoice(statusName, documentationChecklist);
  const statusReadyForInvoice = INVOICE_ALLOWED_STATUSES.some((status) =>
    isDocumentationStatusName(statusName, status)
  );

  // Fetch company data for logo (same as EditInvoicePage)
  const { data: companyData } = useQuery({
    queryKey: ['company'],
    queryFn: () => apiService.getCompany(),
  });

  // Utility function to clean HTML tags from text (copied from EditInvoicePage)
  const cleanHtmlTags = (text: string): string => {
    if (!text) return '';
    return text.replace(/<[^>]*>/g, '').trim();
  };

  // Convert InvoiceDto to Invoice format for the component (copied from EditInvoicePage)
  const convertToInvoiceFormat = (data: InvoiceDto): any => {
    const productLines = data.items?.map(item => ({
      description: cleanHtmlTags(item.description),
      quantity: item.quantity.toString(),
      rate: item.rate.toString(),
    })) || [];

    // Build client address string with phone and email
    const clientAddressParts = [];
    console.log('Client data for PDF:', {
      clientAddress: data.clientAddress,
      clientCity: data.clientCity,
      clientState: data.clientState,
      clientZipCode: data.clientZipCode,
      clientPhone: data.clientPhone,
      clientEmail: data.clientEmail
    });

    if (data.clientAddress) clientAddressParts.push(data.clientAddress);
    if (data.clientCity || data.clientState || data.clientZipCode) {
      const cityStateZip = [data.clientCity, data.clientState, data.clientZipCode]
        .filter(Boolean)
        .join(', ');
      clientAddressParts.push(cityStateZip);
    }
    if (data.clientPhone) clientAddressParts.push(data.clientPhone);
    if (data.clientEmail) clientAddressParts.push(data.clientEmail);

    console.log('Client address parts:', clientAddressParts);

    // Handle company data and logo (same as EditInvoicePage)
    const company = companyData?.data || companyData;
    const logoPath = company?.logoUrl || '';

    // Convert logo path to full URL for @react-pdf/renderer
    const logoUrl = logoPath ? `${window.location.protocol}//${window.location.hostname}:5269/uploads/${logoPath}` : '';

    return {
      logo: logoUrl,
      logoWidth: 100,
      title: `Invoice: ${data.invoiceNumber}`,
      workOrderNumber: data.workOrder?.workOrderNumberFormatted || data.workOrder?.workOrderNumber || '',
      companyName: data.companyName || 'Your Company',
      name: data.companyName || 'Your Company',
      companyAddress: data.companyAddress || 'Your Address',
      companyAddress2: `${data.companyCity || ''}, ${data.companyState || ''} ${data.companyZipCode || ''}`.trim().replace(/^,\s*/, ''),
      companyCountry: 'United States',
      billTo: 'Bill To',
      clientName: `${data.clientName}${data.customerCompany ? ` - ${data.customerCompany}` : ''}`,
      clientAddress: clientAddressParts.join('\n'),
      clientAddress2: '',
      invoiceTitleLabel: 'Invoice #',
      invoiceTitle: data.invoiceNumber,
      invoiceDateLabel: 'Date',
      invoiceDate: new Date(data.invoiceDate).toLocaleDateString(),
      invoiceDueDateLabel: 'Due Date',
      invoiceDueDate: new Date(data.dueDate).toLocaleDateString(),
      productLineDescription: 'Description',
      productLineQuantity: 'Qty',
      productLineQuantityRate: 'Rate',
      productLineQuantityAmount: 'Amount',
      productLines: productLines,
      subTotalLabel: 'Subtotal',
      taxLabel: `Tax (${(data.taxRate * 100).toFixed(1)}%)`,
      totalLabel: 'Total',
      currency: '$',
      notesLabel: 'Notes',
      notes: cleanHtmlTags(data.notes || ''),
      termLabel: 'Terms',
      term: cleanHtmlTags(data.terms || ''),
    };
  };

  const handleDownloadPdf = async (invoiceId: number, invoiceNumber: string) => {
    try {
      setDownloadingId(invoiceId);
      const token = localStorage.getItem('token');
      const baseUrl = process.env.REACT_APP_API_URL || `${window.location.protocol}//${window.location.hostname}:5269/api`;
      const url = `${baseUrl}/invoices/${invoiceId}/pdf${token ? `?access_token=${encodeURIComponent(token)}` : ''}`;
      window.open(url, '_blank');
    } finally {
      setDownloadingId(null);
    }
  };

  const formatCurrency = (amount: number | undefined | null) => {
    if (amount === undefined || amount === null || isNaN(amount)) {
      return '$0.00';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleMarkAsSent = async (invoiceId: number) => {
    try {
      setSendingId(invoiceId);
      await apiService.markInvoiceAsSent(invoiceId);
      // naive refresh: reload page data (parent likely refetches via query; fallback to window reload)
      if (typeof window !== 'undefined') {
        // Prefer soft refresh patterns if available; for now, reload
        window.location.reload();
      }
    } finally {
      setSendingId(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusColors = {
      'Draft': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
      'Sent': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'Paid': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'Overdue': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      'Cancelled': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
    };
    
    return statusColors[status as keyof typeof statusColors] || statusColors['Draft'];
  };

  return (
    <div className="space-y-6 pb-8">
      <EstimatePanel
        workOrderId={workOrderId}
        nteAmount={nteAmount}
        vendors={vendors}
        defaultClientEmail={defaultClientEmail}
        canConvertToInvoice={canCreateInvoice}
        onViewInvoice={onViewInvoice}
      />

      {!canCreateInvoice && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-800 p-4">
          <p className="font-medium text-gray-900 dark:text-gray-100">
            {invoices.length > 0 ? 'Additional invoicing is not available yet' : 'Invoicing is not available yet'}
          </p>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {invoices.length > 0
              ? 'A draft invoice may already exist from an estimate conversion. You can view it below, but new invoices must wait until the work order is ready.'
              : 'This applies to Create Invoice and Convert to Invoice on approved estimates.'}
          </p>
          <ul className="mt-2 text-sm text-gray-700 dark:text-gray-300 space-y-1 list-disc list-inside">
            {!statusReadyForInvoice && (
              <li>Work order must be in {INVOICE_ALLOWED_STATUSES.join(', ')} status.</li>
            )}
            {documentationChecklist && !documentationChecklist.isComplete && (
              <li>All documentation checklist items must be complete.</li>
            )}
            {documentationChecklist?.issues
              .filter((issue) => issue.severity === 'Blocking')
              .map((issue) => (
                <li key={`${issue.code}-${issue.message}`}>{issue.message}</li>
              ))}
          </ul>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Financial Overview</h2>
        <div className="flex items-center space-x-2">
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              disabled={!canCreateInvoice}
              onClick={(e) => {
                if (!canCreateInvoice) return;
                e.stopPropagation();
                setShowInvoiceDropdown(!showInvoiceDropdown);
              }}
              className="flex items-center space-x-2"
              title={!canCreateInvoice ? 'Complete documentation and move to Ready to Invoice first' : undefined}
            >
              <Receipt className="h-4 w-4" />
              <span>Create Invoice</span>
              <ChevronDown className="h-4 w-4" />
            </Button>

            {showInvoiceDropdown && canCreateInvoice && (
              <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                <div className="py-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onCreateDepositInvoice();
                      setShowInvoiceDropdown(false);
                    }}
                    className={`w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between ${
                      suggestedType === 'deposit' ? 'bg-accent-color-50 dark:bg-accent-color-900' : ''
                    }`}
                  >
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">Deposit</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Initial payment before work begins</div>
                    </div>
                    {suggestedType === 'deposit' && (
                      <Badge variant="secondary" className="text-xs">Suggested</Badge>
                    )}
                  </button>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onCreateProgressInvoice();
                      setShowInvoiceDropdown(false);
                    }}
                    className={`w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between ${
                      suggestedType === 'progress' ? 'bg-accent-color-50 dark:bg-accent-color-900' : ''
                    }`}
                  >
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">Progress</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Partial billing during work</div>
                    </div>
                    {suggestedType === 'progress' && (
                      <Badge variant="secondary" className="text-xs">Suggested</Badge>
                    )}
                  </button>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onCreateFinalInvoice();
                      setShowInvoiceDropdown(false);
                    }}
                    className={`w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between ${
                      suggestedType === 'final' ? 'bg-accent-color-50 dark:bg-accent-color-900' : ''
                    }`}
                  >
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">Final</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Final invoice upon completion</div>
                    </div>
                    {suggestedType === 'final' && (
                      <Badge variant="secondary" className="text-xs">Suggested</Badge>
                    )}
                  </button>
                  
                  <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onCreateChangeOrderInvoice();
                      setShowInvoiceDropdown(false);
                    }}
                    className={`w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between ${
                      suggestedType === 'change-order' ? 'bg-accent-color-50 dark:bg-accent-color-900' : ''
                    }`}
                  >
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">Change Order</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Additional work or modifications</div>
                    </div>
                    {suggestedType === 'change-order' && (
                      <Badge variant="secondary" className="text-xs">Suggested</Badge>
                    )}
                  </button>
                  
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Total Invoices</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{invoiceSummary.count}</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <DollarSign className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Total Invoiced</div>
                <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(invoiceSummary.totalInvoiced)}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <CreditCard className="h-5 w-5 text-green-500 dark:text-green-400" />
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Total Paid</div>
                <div className="text-lg font-semibold text-green-700 dark:text-green-300">{formatCurrency(invoiceSummary.totalPaid)}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <TrendingUp className="h-5 w-5 text-orange-500 dark:text-orange-400" />
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Outstanding</div>
                <div className="text-lg font-semibold text-orange-700 dark:text-orange-300">{formatCurrency(invoiceSummary.outstanding)}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* NTE Information */}
      {nteAmount && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <FileText className="h-5 w-5" />
              <span>Not To Exceed (NTE)</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {formatCurrency(nteAmount)}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Maximum amount for this work order
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invoices List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Receipt className="h-5 w-5" />
            <span>Invoices</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Receipt className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <div className="text-sm">No invoices created yet</div>
              <div className="text-xs">Create invoices to track billing</div>
            </div>
          ) : (
            <div className="space-y-3">
              {invoices.map((invoice) => (
                <div key={invoice.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex items-center space-x-4 flex-1 min-w-0">
                    <div className="text-gray-500 dark:text-gray-400">
                      <Receipt className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {invoice.invoiceNumber}
                        </div>
                        <Badge className={`text-xs ${getStatusBadge(invoice.status)}`}>
                          {invoice.status}
                        </Badge>
                        {invoice.invoiceType && (
                          <Badge variant="outline" className="text-xs">
                            {invoice.invoiceType}
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Created: {formatDate(invoice.invoiceDate)}
                        {invoice.dueDate && ` • Due: ${formatDate(invoice.dueDate)}`}
                        {invoice.sentAt && ` • Sent: ${formatDate(invoice.sentAt)}`}
                        {invoice.paidAt && ` • Paid: ${formatDate(invoice.paidAt)}`}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {formatCurrency(invoice.total)}
                      </div>
                      {invoice.paidAmount && invoice.paidAmount > 0 && (
                        <div className="text-xs text-green-600 dark:text-green-400">
                          Paid: {formatCurrency(invoice.paidAmount)}
                        </div>
                      )}
                    </div>
                    <div className="relative">
                      <button 
                        className="inline-flex items-center p-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveDropdownId(activeDropdownId === `invoice-${invoice.id}` ? null : `invoice-${invoice.id}`);
                        }}
                      >
                        <MoreVertical className="h-4 w-4" />
                      </button>
                      
                      {activeDropdownId === `invoice-${invoice.id}` && (
                        <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                          <div className="py-1">
                            <button
                              onClick={() => {
                                setActiveDropdownId(null);
                                handleDownloadPdf(invoice.id, invoice.invoiceNumber);
                              }}
                              disabled={downloadingId === invoice.id}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-2 disabled:opacity-50"
                            >
                              <Download className="h-4 w-4" />
                              <span>{downloadingId === invoice.id ? 'Downloading…' : 'Download PDF'}</span>
                            </button>
                            <button
                              onClick={() => {
                                setActiveDropdownId(null);
                                handleMarkAsSent(invoice.id);
                              }}
                              disabled={sendingId === invoice.id}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-2 disabled:opacity-50"
                            >
                              <Mail className="h-4 w-4" />
                              <span>{sendingId === invoice.id ? 'Sending…' : 'Mark as Sent'}</span>
                            </button>
                            <button
                              onClick={() => {
                                setActiveDropdownId(null);
                                onViewInvoice(invoice.id);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-2"
                            >
                              <Eye className="h-4 w-4" />
                              <span>View Invoice</span>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
};

export default FinancialTab;
